import React, { useRef, useEffect, useState } from 'react';
import Icon from '../AppIcon';

const NativeVideoPlayer = ({ 
  url, 
  config = {}, 
  onTimeUpdate, 
  onComplete, 
  currentTime, 
  duration,
  className = "w-full h-full"
}) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleLoadedData = () => setIsLoading(false);
    const handleTimeUpdate = () => {
      if (onTimeUpdate) {
        onTimeUpdate(video?.currentTime, video?.duration);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      if (onComplete) onComplete();
    };

    video?.addEventListener('loadeddata', handleLoadedData);
    video?.addEventListener('timeupdate', handleTimeUpdate);
    video?.addEventListener('ended', handleEnded);

    return () => {
      video?.removeEventListener('loadeddata', handleLoadedData);
      video?.removeEventListener('timeupdate', handleTimeUpdate);
      video?.removeEventListener('ended', handleEnded);
    };
  }, [url, onTimeUpdate, onComplete]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video && currentTime !== video?.currentTime) {
      video.currentTime = currentTime;
    }
  }, [currentTime]);

  const togglePlay = () => {
    const video = videoRef?.current;
    if (!video) return;

    if (isPlaying) {
      video?.pause();
    } else {
      video?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(handleMouseMove.timeout);
    handleMouseMove.timeout = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  // Debug: log da URL recebida
  console.log('🔍 NativeVideoPlayer Debug:', {
    url,
    hasUrl: !!url,
    urlType: typeof url
  });

  if (!url) {
    console.log('❌ NativeVideoPlayer: URL não fornecida');
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>URL do vídeo não fornecida</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${className} relative bg-black`}
      onMouseMove={handleMouseMove}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="flex flex-col items-center space-y-4 text-white">
            <div className="animate-spin">
              <Icon name="Loader2" size={40} />
            </div>
            <p>Carregando vídeo...</p>
          </div>
        </div>
      )}

      <video
        ref={videoRef}
        src={url}
        className="w-full h-full"
        onClick={togglePlay}
        controls={false}
      />

      {/* Play/Pause Overlay */}
      {!isLoading && (
        <div 
          className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-opacity cursor-pointer ${
            showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={togglePlay}
        >
          {!isPlaying && (
            <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-tier-2">
              <Icon name="Play" size={32} className="text-primary ml-1" />
            </div>
          )}
        </div>
      )}

      {/* Video Controls */}
      {!isLoading && showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-4">
              <button
                onClick={togglePlay}
                className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              >
                <Icon name={isPlaying ? "Pause" : "Play"} size={20} />
              </button>
              <span className="text-sm">Vídeo Nativo</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Maximize" size={20} className="cursor-pointer hover:text-gray-300" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NativeVideoPlayer;
